import {
  CmsRegionFieldsFragment,
  CmsRegionWithExcludedIndustriesFieldsFragment,
  CraftsmenRegionDirectoryPageFieldsFragment,
  CraftsmenRegionPageFieldsFragment,
  IndustryOverviewPageSideNavFieldsFragment,
  IndustryRegionPageFieldsFragment,
  RegionalIndustryFieldsFragment,
  RelatedRegionFieldsFragment,
} from '../../../../__generated__/datocms.types'
import {validateDatoCmsTypes} from './common.serde'
import {
  CraftsmenRegionDirectoryPageContent,
  CraftsmenRegionPage,
  CraftsmenRegionPageContent,
  FeaturedIndustry,
  IndustryRegionPage,
  IndustryRegionPageContent,
  Region,
  RegionalIndustry,
  RegionWithExcludedIndustries,
  RelatedRegion,
  SideNavigationIndustryItem,
} from '../types/craftsmen-region-types'
import {parseReviewSection} from './review-section.serde'
import {parseSupportSection} from './support-section.serde'
import {parseKnowledgeSection} from './knowledge-section.serde'
import {parsePresentationSection} from './presentation-section.serde'
import {parsePromotionalBannerSection} from './promotional-banner-section.serde'
import {parseInspirationSection} from './inspiration-section.serde'
import {LinkService} from '../../i18n/link.service'
import {parsePageMeta} from './seo.serde'
import {parseContentSection} from './content-section.serde'
import {parseSignupSection} from './signup-section.serde'
import {parseProMapSection} from './pro-map-section.serde'
import {parseRelatedRegionSection} from './related-region-section.serde'
import {parseRelatedIndustrySection} from './related-industry-section.serde'
import {parseRelatedArticlesSection} from './related-articles-section.serde'
import {parseVideoSection} from './video-section.serde'

export function parseCraftsmenRegionPage(
  linkService: LinkService,
  e: CraftsmenRegionPageFieldsFragment,
): CraftsmenRegionPage {
  const entry = validateDatoCmsTypes(e)
  const page = validateDatoCmsTypes(entry)

  const content: CraftsmenRegionPageContent[] | undefined = page.content
    ?.map((c): CraftsmenRegionPageContent | undefined => {
      switch (c?.__typename) {
        case 'ReviewSectionModelRecord':
          return parseReviewSection(c)
        case 'SupportSectionModelRecord':
          return parseSupportSection(c)
        case 'KnowledgeSectionModelRecord':
          return parseKnowledgeSection(c)
        case 'PresentationSectionModelRecord':
          return parsePresentationSection(c)
        case 'FieldContentSectionModelRecord':
          return parseContentSection(c)
        case 'FieldRegionPageProDirectoryRecord':
          return {__type: 'ProDirectory'}
        case 'FieldSignupSectionRecord':
          return parseSignupSection(c)
        case 'FieldProMapSectionRecord':
          return parseProMapSection(c)
        case 'FieldRelatedRegionsSectionRecord':
          return parseRelatedRegionSection(c)
        case 'VideoSectionRecord':
          return parseVideoSection(c)
        default:
          return undefined
      }
    })
    .filter((c): c is CraftsmenRegionPageContent => !!c)
  const linkedIndustries: FeaturedIndustry[] = page.featuredIndustries
    .filter((i): i is FeaturedIndustry => !!i)
    .map(i => ({
      label: i.label,
      slug: i.slug,
      hideInGeneratedPages: i.hideInGeneratedPages || false,
    }))

  return {
    cover: {
      title: page!.coverTitle,
      subtitle: page!.coverSubtitle,
    },
    content: content || [],
    linkedIndustries,
    seo: parsePageMeta(linkService, page.seo),
  }
}

export function parseIndustryRegionPage(
  linkService: LinkService,
  e: IndustryRegionPageFieldsFragment,
  relatedIndustries: RegionalIndustryFieldsFragment[],
  relatedTopics: string[],
  replaceHeadingPlaceHolders: (value: string) => string,
): IndustryRegionPage {
  const entry = validateDatoCmsTypes(e)
  const page = validateDatoCmsTypes(entry)

  const content: IndustryRegionPageContent[] = page.content
    ?.map((c): IndustryRegionPageContent | undefined => {
      switch (c.__typename) {
        case 'PresentationSectionModelRecord':
          return parsePresentationSection(c)
        case 'ReviewSectionModelRecord':
          return parseReviewSection(c)
        case 'SupportSectionModelRecord':
          return parseSupportSection(c)
        case 'FieldContentSectionModelRecord':
          return parseContentSection(c)
        case 'FieldRegionPageProDirectoryRecord':
          return {__type: 'ProDirectory'}
        case 'FieldSignupSectionRecord':
          return parseSignupSection(c)
        case 'FieldProMapSectionRecord':
          return parseProMapSection(c)
        case 'FieldRelatedRegionsSectionRecord':
          return parseRelatedRegionSection(c)
        case 'FieldRelatedIndustriesSectionRecord':
          return parseRelatedIndustrySection(c, relatedIndustries)
        case 'FieldRelatedArticlesSectionRecord':
          return parseRelatedArticlesSection(c, relatedTopics, replaceHeadingPlaceHolders)
        case 'VideoSectionRecord':
          return parseVideoSection(c)
        default: {
          return undefined
        }
      }
    })
    .filter((c): c is IndustryRegionPageContent => !!c)

  return {
    cover: {
      title: page!.coverTitle,
      subtitle: page!.coverSubtitle,
    },
    content: content || [],
    seo: parsePageMeta(linkService, page.seo),
  }
}

export function parseRegion(e: CmsRegionFieldsFragment): Region {
  const entry = validateDatoCmsTypes(e)
  const postalCodesString = entry.postalCodes ?? ''
  const postalCodes = postalCodesString.split(',').map(p => Number(p))
  return {
    cmsId: entry.cmsId,
    regionName: entry.clientFacingLabel,
    postalCodes,
    relatedRegions: entry.relatedRegions.map(parseRelatedRegion),
    slug: entry.slug,
    doNotIndex: entry.disableRegionSeoIndexing,
  }
}

export function parseRegionWithExcludedIndustries(
  e: CmsRegionWithExcludedIndustriesFieldsFragment,
): RegionWithExcludedIndustries {
  const region = parseRegion(e)

  return {
    ...region,
    excludedIndustryIds: e.excludedIndustries.map(i => i.id),
  }
}

export function parseRegionalIndustry(e: RegionalIndustryFieldsFragment): RegionalIndustry {
  const cmsIndustry = validateDatoCmsTypes(e)

  return {
    id: cmsIndustry.id,
    cmsId: cmsIndustry.cmsId,
    slug: cmsIndustry.slug,
    label: cmsIndustry.label,
    textVariable: cmsIndustry.textVariable,
    hideInGeneratedPages: cmsIndustry.hideInGeneratedPages || false,
  }
}

export function parseRegionDirectoryPage(e: CraftsmenRegionDirectoryPageFieldsFragment): {
  content: CraftsmenRegionDirectoryPageContent[]
  excludedRegionalIndustries: RegionalIndustry[]
} {
  const entry = validateDatoCmsTypes(e)
  const page = validateDatoCmsTypes(entry)

  const content: CraftsmenRegionDirectoryPageContent[] = page.content
    ?.map((c): CraftsmenRegionDirectoryPageContent | undefined => {
      switch (c.__typename) {
        case 'PromotionBannerModelRecord':
          return parsePromotionalBannerSection(c)
        case 'ReviewSectionModelRecord':
          return parseReviewSection(c)
        case 'KnowledgeSectionModelRecord':
          return parseKnowledgeSection(c)
        case 'InspirationSectionModelRecord':
          return parseInspirationSection(c)
        case 'FieldContentSectionModelRecord':
          return parseContentSection(c)
        case 'FieldSignupSectionRecord':
          return parseSignupSection(c)
        case 'VideoSectionRecord':
          return parseVideoSection(c)
        default:
          return undefined
      }
    })
    .filter((c): c is CraftsmenRegionDirectoryPageContent => !!c)

  const cmsExcludedRegionalIndustries = validateDatoCmsTypes(page.excludedIndustries)
  const excludedRegionalIndustries = cmsExcludedRegionalIndustries.map(t => parseRegionalIndustry(t))

  return {content, excludedRegionalIndustries}
}

export function parseSideNavigationIndustryOverviewPageItem(
  e: IndustryOverviewPageSideNavFieldsFragment[],
): SideNavigationIndustryItem[] {
  const entry = validateDatoCmsTypes(e)
  return entry
    .filter(i => i?.slug && i?.proDirectoryIndustries?.length)
    .map(i => {
      const validatedIndustry = validateDatoCmsTypes(i)
      return {
        slug: validatedIndustry.slug,
        title: validatedIndustry.breadcrumbTitle?.length ? validatedIndustry.breadcrumbTitle : validatedIndustry.title,
      }
    })
}

export function parseRelatedRegion(e: RelatedRegionFieldsFragment): RelatedRegion {
  const entry = validateDatoCmsTypes(e)
  return {
    regionName: entry.clientFacingLabel,
    slug: entry.slug,
  }
}
